import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

/**
 * Custom hook to handle accessibility concerns when routes change.
 *
 * When a route changes:
 * 1. Scrolls the page to the top
 * 2. Moves focus to the main content area
 * 3. Announces the page change to screen readers
 *
 * This improves navigation experience for keyboard users and screen reader users
 * by ensuring they start at a consistent position on each new page.
 */
export const useRouteChangeAccessibility = () => {
  const location = useLocation();
  const { t } = useTranslation('components', { keyPrefix: 'appLayout.routeChange' });

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto',
      });

      const startContent = document.querySelector<HTMLElement>('#start');
      if (startContent) {
        startContent.setAttribute('tabindex', '-1');
        startContent.focus();

        setTimeout(() => {
          startContent.removeAttribute('tabindex');
        }, 100);
      }

      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.className = 'sr-only';
      announcement.textContent = t('announcement');

      document.body.append(announcement);

      setTimeout(() => {
        if (document.body.contains(announcement)) {
          announcement.remove();
        }
      }, 1000);
    }, 50);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [location.pathname, t]);
};
